import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api, getUser, clearSession } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Phone, MapPin, Calendar, Bus, LogOut, Play, CheckCircle2, Loader2, KeyRound, ShieldCheck, X, UserCog, MessageCircle, IdCard, Camera, CheckCheck } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PasswordInput from "../components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const STATUS_COLOR = {
  pending: "bg-slate-100 text-slate-600",
  confirmed: "bg-blue-100 text-blue-700",
  vehicle_assigned: "bg-indigo-100 text-indigo-700",
  driver_assigned: "bg-violet-100 text-violet-700",
  trip_started: "bg-amber-100 text-amber-800",
  trip_completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
  no_show: "bg-slate-200 text-slate-500",
};

export default function DriverDashboard() {
  const [scope, setScope] = useState("upcoming");
  const [items, setItems] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinForm, setPinForm] = useState({ new_pin: "", confirm: "" });
  const [credsOpen, setCredsOpen] = useState(false);
  const [credsForm, setCredsForm] = useState({ email: "", password: "" });
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ whatsapp: "", license_expiry: "" });
  const nav = useNavigate();
  const user = getUser();

  const load = () => api.get(`/drivers/me/bookings?scope=${scope}`).then(r => setItems(r.data)).catch(() => {});

  useEffect(() => { api.get("/drivers/me").then(r => setMe(r.data)).catch(() => nav("/driver/login")); }, [nav]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const mark = async (bid, status) => {
    setBusy(bid);
    try {
      await api.patch(`/bookings/${bid}/driver-status`, { status });
      toast.success(status === "trip_started" ? "Trip started" : "Trip completed. Great work!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    setBusy(null);
  };

  const logout = () => { clearSession(); nav("/driver/login"); };

  const savePin = async () => {
    if (pinForm.new_pin.length !== 4 || !/^\d{4}$/.test(pinForm.new_pin)) { toast.error("PIN must be 4 digits"); return; }
    if (pinForm.new_pin !== pinForm.confirm) { toast.error("PINs do not match"); return; }
    try {
      await api.patch("/drivers/me/pin", { new_pin: pinForm.new_pin });
      toast.success("PIN updated. Use the new PIN next time you sign in.");
      setPinOpen(false); setPinForm({ new_pin: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveCreds = async () => {
    if (!credsForm.email || credsForm.password.length < 6) { toast.error("Enter email and password ≥ 6 chars"); return; }
    try {
      await api.patch("/drivers/me/credentials", credsForm);
      toast.success("Email + password updated. You can now log in with either.");
      setCredsOpen(false); setCredsForm({ email: "", password: "" });
      api.get("/drivers/me").then(r => setMe(r.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveProfile = async () => {
    try {
      const r = await api.patch("/drivers/me/profile", {
        whatsapp: profileForm.whatsapp || null,
        license_expiry: profileForm.license_expiry || null,
      });
      setMe(r.data);
      toast.success("Profile updated. Ops now sees your latest details.");
      setProfileOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update profile"); }
  };

  const [uploadingLic, setUploadingLic] = useState(false);
  const uploadLicense = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { toast.error("JPEG, PNG, or WebP only"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("File too large (max 3 MB)"); return; }
    setUploadingLic(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/drivers/me/license-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMe(r.data);
      toast.success("License photo uploaded. Ops can now verify your license.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setUploadingLic(false);
  };

  const licenseWarn = (() => {
    if (!me?.license_expiry) return null;
    const d = new Date(me.license_expiry);
    if (Number.isNaN(d.getTime())) return null;
    const days = Math.round((d - new Date()) / 86400000);
    if (days < 0) return { level: "expired", days: Math.abs(days) };
    if (days <= 30) return { level: "soon", days };
    return null;
  })();

  return (
    <PublicLayout>
      <section className="max-w-4xl mx-auto px-4 pt-8 pb-16">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#ea580c] font-semibold">Driver Dashboard</div>
            <h1 className="text-3xl md:text-4xl font-display font-black tracking-tight" data-testid="driver-hello">Hi, {me?.name || user?.name || "Driver"}</h1>
            <p className="text-slate-500 mt-1 text-sm">Your assigned trips at a glance.</p>
          </div>
          <Button variant="outline" onClick={() => { setProfileForm({ whatsapp: me?.whatsapp || "", license_expiry: me?.license_expiry || "" }); setProfileOpen(true); }} data-testid="driver-edit-profile"><UserCog className="w-4 h-4 mr-1 text-emerald-600" /> Edit Profile</Button>
          <Button variant="outline" onClick={() => setPinOpen(true)} data-testid="driver-change-pin"><KeyRound className="w-4 h-4 mr-1 text-amber-600" /> Change PIN</Button>
          <Button variant="outline" onClick={() => { setCredsForm({ email: me?.email || "", password: "" }); setCredsOpen(true); }} data-testid="driver-change-creds"><ShieldCheck className="w-4 h-4 mr-1 text-blue-600" /> Email &amp; Password</Button>
          <Button variant="outline" onClick={logout} data-testid="driver-logout"><LogOut className="w-4 h-4 mr-1" /> Sign out</Button>
        </div>

        <div className="flex gap-2 mb-4">
          {[["today", "Today"], ["upcoming", "Upcoming"], ["all", "All"]].map(([k, l]) => (
            <button key={k} onClick={() => setScope(k)} className={`px-4 py-1.5 rounded-full text-sm font-semibold border ${scope === k ? "bg-[var(--brand-navy)] text-white border-[var(--brand-navy)]" : "bg-white text-slate-700 border-slate-300 hover:border-[#ea580c]"}`} data-testid={`driver-scope-${k}`}>{l}</button>
          ))}
        </div>

        {licenseWarn && (
          <div className={`mb-4 rounded-2xl border p-3 text-sm flex items-start gap-2 ${licenseWarn.level === "expired" ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="driver-license-warn">
            <IdCard className={`w-4 h-4 mt-0.5 ${licenseWarn.level === "expired" ? "text-rose-600" : "text-amber-600"}`} />
            <div className="flex-1">
              <div className="font-semibold">
                {licenseWarn.level === "expired"
                  ? `Your driving license expired ${licenseWarn.days} day${licenseWarn.days === 1 ? "" : "s"} ago.`
                  : `Your driving license expires in ${licenseWarn.days} day${licenseWarn.days === 1 ? "" : "s"}.`}
              </div>
              <div className="text-xs opacity-80">Update the new expiry date once renewed so ops can keep assigning you trips.</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => { setProfileForm({ whatsapp: me?.whatsapp || "", license_expiry: me?.license_expiry || "" }); setProfileOpen(true); }} data-testid="driver-license-fix">Update</Button>
          </div>
        )}

        {items.length === 0 ? (
          <div className="p-10 text-center bg-white border border-slate-200 rounded-2xl text-slate-500" data-testid="driver-empty">
            <Bus className="w-10 h-10 mx-auto text-slate-300" />
            <div className="font-display font-bold text-slate-700 mt-3">No trips {scope === "today" ? "today" : scope === "upcoming" ? "upcoming" : "yet"}</div>
            <div className="text-xs mt-1">Once ops assigns you a trip, it will appear here.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(b => (
              <div key={b.id} className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm" data-testid={`driver-booking-${b.booking_id}`}>
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <div className="font-mono text-xs text-slate-500">{b.booking_id}</div>
                    <div className="font-display font-black text-xl text-[var(--brand-navy)]">{b.from_place} → {b.to_place}</div>
                  </div>
                  <div className={`text-[11px] px-2 py-0.5 rounded-full font-semibold capitalize ${STATUS_COLOR[b.status] || "bg-slate-100 text-slate-600"}`}>{b.status.replace(/_/g, " ")}</div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm">
                  <div className="flex items-start gap-1.5"><Calendar className="w-4 h-4 text-[#ea580c] mt-0.5" /><div><div className="text-[11px] text-slate-500">Travel</div>{b.travel_date} {b.departure_time && `· ${b.departure_time}`}</div></div>
                  <div className="flex items-start gap-1.5"><Bus className="w-4 h-4 text-[#ea580c] mt-0.5" /><div><div className="text-[11px] text-slate-500">Vehicle</div>{b.vehicle?.vehicle_number || "—"} · {b.vehicle?.vehicle_type || b.vehicle_type_pref || "TBD"}</div></div>
                  <div className="flex items-start gap-1.5"><MapPin className="w-4 h-4 text-[#ea580c] mt-0.5" /><div><div className="text-[11px] text-slate-500">Pickup</div>{b.pickup_location || "—"}</div></div>
                  <div className="flex items-start gap-1.5"><Phone className="w-4 h-4 text-[#ea580c] mt-0.5" /><div><div className="text-[11px] text-slate-500">Passenger</div>{b.customer_name} · {b.passengers} pax</div></div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={`tel:${b.customer_mobile}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#25D366] hover:bg-emerald-600 text-white text-xs font-bold" data-testid={`driver-call-${b.booking_id}`}><Phone className="w-3.5 h-3.5" /> Call {b.customer_mobile}</a>
                  {b.status !== "trip_started" && b.status !== "trip_completed" && (
                    <Button size="sm" onClick={() => mark(b.booking_id, "trip_started")} disabled={busy === b.booking_id} className="bg-amber-500 hover:bg-amber-600 text-white h-8 rounded-full px-3 text-xs font-bold" data-testid={`driver-start-${b.booking_id}`}>
                      {busy === b.booking_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />} Start Trip
                    </Button>
                  )}
                  {b.status === "trip_started" && (
                    <Button size="sm" onClick={() => mark(b.booking_id, "trip_completed")} disabled={busy === b.booking_id} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 rounded-full px-3 text-xs font-bold" data-testid={`driver-complete-${b.booking_id}`}>
                      {busy === b.booking_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Mark Completed
                    </Button>
                  )}
                  <Link to={`/book/confirm/${b.booking_id}`} className="inline-flex items-center px-3 py-1.5 rounded-full border border-slate-300 hover:border-[#ea580c] hover:text-[#ea580c] text-xs font-semibold" data-testid={`driver-details-${b.booking_id}`}>View details</Link>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={pinOpen} onOpenChange={setPinOpen}>
          <DialogContent data-testid="driver-pin-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-600" /> Change your PIN</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500 -mt-2">Pick a new 4-digit PIN. You'll use this to sign in next time. No verification of the old PIN is required.</p>
            <div><Label>New 4-digit PIN</Label><Input inputMode="numeric" maxLength={4} value={pinForm.new_pin} onChange={e => setPinForm({ ...pinForm, new_pin: e.target.value.replace(/\D/g, "") })} data-testid="driver-new-pin" /></div>
            <div><Label>Confirm PIN</Label><Input inputMode="numeric" maxLength={4} value={pinForm.confirm} onChange={e => setPinForm({ ...pinForm, confirm: e.target.value.replace(/\D/g, "") })} data-testid="driver-confirm-pin" /></div>
            <DialogFooter><Button onClick={savePin} className="bg-amber-500 hover:bg-amber-600" data-testid="driver-save-pin">Save new PIN</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={credsOpen} onOpenChange={setCredsOpen}>
          <DialogContent data-testid="driver-creds-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" /> Email &amp; password</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500 -mt-2">Set (or update) your login email and password. You can also keep signing in with mobile + PIN as before.</p>
            <div><Label>Email</Label><Input type="email" value={credsForm.email} onChange={e => setCredsForm({ ...credsForm, email: e.target.value })} data-testid="driver-new-email" /></div>
            <div><Label>Password (min 6 chars)</Label><PasswordInput value={credsForm.password} onChange={e => setCredsForm({ ...credsForm, password: e.target.value })} data-testid="driver-new-password" /></div>
            <DialogFooter><Button onClick={saveCreds} className="bg-blue-600 hover:bg-blue-700" data-testid="driver-save-creds">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
          <DialogContent data-testid="driver-profile-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCog className="w-5 h-5 text-emerald-600" /> Edit profile</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500 -mt-2">Keep your WhatsApp and license expiry current — ops uses these to route trip confirmations to you and to flag renewals in advance.</p>
            <div>
              <Label className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5 text-emerald-600" /> WhatsApp number</Label>
              <Input placeholder="+91XXXXXXXXXX" value={profileForm.whatsapp} onChange={e => setProfileForm({ ...profileForm, whatsapp: e.target.value })} data-testid="driver-profile-whatsapp" />
              <div className="text-[11px] text-slate-500 mt-1">Leave blank to use your login mobile ({me?.mobile}).</div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><IdCard className="w-3.5 h-3.5 text-amber-600" /> License expiry (YYYY-MM-DD)</Label>
              <Input type="date" value={profileForm.license_expiry} onChange={e => setProfileForm({ ...profileForm, license_expiry: e.target.value })} data-testid="driver-profile-license" />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Camera className="w-3.5 h-3.5 text-emerald-600" /> License photo</Label>
              <div className="flex items-center gap-3 mt-1">
                <label className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-xs font-semibold cursor-pointer ${uploadingLic ? "bg-slate-100 text-slate-400" : "bg-white border-slate-300 hover:border-emerald-500 text-slate-700"}`} data-testid="driver-license-upload-label">
                  {uploadingLic ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5 text-emerald-600" />}
                  {me?.license_photo_path ? "Replace photo" : "Upload photo"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => uploadLicense(e.target.files?.[0])} disabled={uploadingLic} data-testid="driver-license-file-input" />
                </label>
                {me?.license_photo_path && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold" data-testid="driver-license-saved">
                    <CheckCheck className="w-3.5 h-3.5" /> Photo on file
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">JPEG / PNG / WebP · max 3 MB. Only you and admin can see this.</div>
            </div>
            <DialogFooter><Button onClick={saveProfile} className="bg-emerald-600 hover:bg-emerald-700" data-testid="driver-save-profile">Save profile</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </PublicLayout>
  );
}
